import { useState, FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../App'
import { api } from '../services/api'
import { generateRegistrationKeys } from '../hooks/useSignalProtocol'

// Helper: ArrayBuffer to base64url
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// Helper: base64url to ArrayBuffer
function base64urlToBuffer(base64url: string): ArrayBuffer {
  let s = base64url.replace(/-/g, '+').replace(/_/g, '/')
  while (s.length % 4) s += '='
  const binary = atob(s)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

export default function RegisterPage() {
  const [username, setUsername] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false)
  const [isBindingPasskey, setIsBindingPasskey] = useState(false)
  const navigate = useNavigate()
  const { login } = useAuth()

  const validateUsername = (value: string): string | null => {
    if (!value.trim()) {
      return 'Please enter a username'
    }
    if (value.length < 3) {
      return 'Username must be at least 3 characters'
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return 'Only letters, numbers, and underscores allowed'
    }
    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const validationError = validateUsername(username)
    if (validationError) {
      setError(validationError)
      return
    }

    if (!agreedToTerms) {
      setError('Please agree to the privacy policy')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const keys = generateRegistrationKeys()
      const response = await api.register(username.trim(), keys)
      const userId = response.user?.id || username.trim()
      login(userId, response.user?.username || username.trim(), response.token)

      if (response.user?.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(response.user.id)) {
        localStorage.setItem('lynkstar_my_uuid', response.user.id)
      }

      // Check if browser supports WebAuthn before prompting
      if (window.PublicKeyCredential) {
        setShowPasskeyPrompt(true)
      } else {
        navigate('/chats')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBindPasskey = async () => {
    setIsBindingPasskey(true)
    setError(null)

    try {
      // 1. Get registration options
      const { options } = await api.getPasskeyRegisterOptions()

      // 2. Create credential
      const publicKeyOptions: CredentialCreationOptions = {
        publicKey: {
          rp: options.rp as PublicKeyCredentialRpEntity,
          user: {
            id: base64urlToBuffer((options.user as any).id),
            name: (options.user as any).name,
            displayName: (options.user as any).displayName,
          },
          challenge: base64urlToBuffer(options.challenge as unknown as string),
          pubKeyCredParams: options.pubKeyCredParams as PublicKeyCredentialParameters[],
          timeout: (options as any).timeout,
          excludeCredentials: ((options as any).excludeCredentials || []).map((cred: any) => ({
            id: base64urlToBuffer(cred.id),
            type: cred.type,
          })),
          authenticatorSelection: (options as any).authenticatorSelection,
          attestation: (options as any).attestation || 'none',
        },
      }

      const credential = await navigator.credentials.create(publicKeyOptions) as PublicKeyCredential | null
      if (!credential) {
        setShowPasskeyPrompt(false)
        navigate('/chats')
        return
      }

      const attestationResponse = credential.response as AuthenticatorAttestationResponse

      // 3. Send to server
      await api.verifyPasskeyRegister({
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(attestationResponse.clientDataJSON),
          attestationObject: bufferToBase64url(attestationResponse.attestationObject),
          transports: (attestationResponse as any).getTransports?.() || undefined,
        },
        device_name: navigator.userAgent.includes('Mobile') ? 'Mobile Device' : 'Desktop',
      })

      navigate('/chats')
    } catch (err) {
      console.error('Passkey binding error:', err)
      // Don't block registration if passkey fails
      navigate('/chats')
    } finally {
      setIsBindingPasskey(false)
      setShowPasskeyPrompt(false)
    }
  }

  const handleSkipPasskey = () => {
    setShowPasskeyPrompt(false)
    navigate('/chats')
  }

  // Passkey binding prompt
  if (showPasskeyPrompt) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-6 py-12">
        <div className="mx-auto w-full max-w-sm">
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-green-500 rounded-2xl flex items-center justify-center shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 text-center">
            Account Created!
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8 text-center">
            Set up a Passkey for faster, more secure sign-in next time
          </p>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl mb-6">
            <div className="flex items-start gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
              </svg>
              <div>
                <h3 className="font-semibold text-blue-700 dark:text-blue-300 mb-1">What is a Passkey?</h3>
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  A Passkey uses your device's biometric (Face ID, fingerprint) or screen lock to verify your identity. It's phishing-resistant and more secure than passwords.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleBindPasskey}
            disabled={isBindingPasskey}
            className="btn-primary w-full flex items-center justify-center gap-2 mb-3"
          >
            {isBindingPasskey ? (
              <>
                <div className="spinner mr-2" />
                Setting up...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                </svg>
                Set Up Passkey
              </>
            )}
          </button>

          <button
            onClick={handleSkipPasskey}
            disabled={isBindingPasskey}
            className="w-full px-4 py-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Create Account
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          Your messages will be end-to-end encrypted. Only you and the recipient can read them.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username Input */}
          <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="input-field pl-12"
                disabled={isLoading}
                autoComplete="username"
                autoFocus
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              This will be your unique identifier
            </p>
          </div>

          {/* Key Generation Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              <span className="font-semibold text-blue-700 dark:text-blue-300">
                Key Generation
              </span>
            </div>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              We will generate secure encryption keys on your device:
            </p>
            <ul className="text-sm text-blue-600 dark:text-blue-400 mt-2 ml-4 list-disc">
              <li>Identity Key - Your unique cryptographic identity</li>
              <li>Pre-Keys - For establishing encrypted sessions</li>
            </ul>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-2 font-medium">
              These keys never leave your device.
            </p>
          </div>

          {/* Privacy Policy Agreement */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="terms" className="text-sm text-gray-600 dark:text-gray-400">
              I agree to the{' '}
              <a href="#" className="text-primary font-medium hover:underline">
                Privacy Policy
              </a>{' '}
              and understand that my messages are end-to-end encrypted
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 text-error text-sm px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          {/* Register Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="spinner mr-2" />
                Generating Keys...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Security Badge */}
        <div className="mt-6 flex items-center justify-center gap-2 text-green-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          <span className="text-sm">Protected by Signal Protocol</span>
        </div>

        {/* Login Link */}
        <div className="mt-8 text-center">
          <span className="text-gray-500 dark:text-gray-400">
            Already have an account?{' '}
          </span>
          <Link
            to="/login"
            className="text-primary font-medium hover:underline"
          >
            Sign In
          </Link>
        </div>

        {/* Brand */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Powered by <span className="font-semibold text-primary">LYNKSTAR</span>
          </p>
        </div>
      </div>
    </div>
  )
}
